import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
	makeStyles,
	Grid,
	Typography,
	Button,
	TextField,
	useMediaQuery,
	useTheme
} from '@material-ui/core';
import MomentCore from 'moment';

import ResponsiveDialog from "./ResponsiveDialog";
import FormHelper from "../utils/FormHelper";
import Constants from "../utils/Constants";
import HTMLEntities from "../utils/HTMLEntities";

const styles = makeStyles(theme => ({
	subtitle: {
		paddingBottom: theme.spacing(3)
	},

	dlgTitle: {
		paddingBottom: 0
	},

	details: {
		overflowY: "auto",
		whiteSpace: "pre-wrap",
		padding: theme.spacing(1),
		marginBottom: theme.spacing(3),
		maxHeight: theme.spacing(31.25),
		background: theme.palette.grey[200],

		"&::-webkit-scrollbar": {
			width: theme.spacing(1),
			backgroundColor: "#e9e9e9"
		},

		"&::-webkit-scrollbar-thumb": {
			borderRadius: 5,
			backgroundColor: "#c2c2c2"
		}
	},

	content: {
		paddingBottom: theme.spacing(4)
	},

	cancelBtnItem: {
		paddingRight: theme.spacing(1)
	},

	link: {
		cursor: "pointer",
		color: theme.palette.primary.main,
		textDecoration: 'none',
		'&:focus': {
			textDecoration: "underline"
		}
	}
}));

function ShareToBtaDialog(props) {
	const theme = useTheme();
	const [notes, setNotes] = useState();
	const [notesObj, setNotesObj] = useState();
	const smaller = useMediaQuery(theme.breakpoints.down("sm"));

	let classes = styles(),
		{open, closeHandler, activity, pattern, anonymize} = props;

	const notesChanged = (event) => {
		setNotes(FormHelper.getChanged(event));
	};

	const buildReport = useCallback(() => {
		if (activity && Object.keys(activity).length > 0) {
			let obj = {incidentDate: MomentCore(activity.published).toISOString()},
				isTwitter = activity.sourceId === Constants.SOURCES.IDS.TWITTER,
				topicsArray = activity.postMatches && activity.postMatches.length ? activity.postMatches.map(t => t.library_topic ? t.library_topic.name : "") : [],
				uniqueTopicsArray = [...new Set(topicsArray)],
				category = activity.category === Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID ?
					Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.NAME : Constants.ACTIVITY.CATEGORIES.ALERTS.NAME,
				isGDoc = activity.sourceId === Constants.SOURCES.IDS.GOOGLE_DOCS && activity.subSourceId !== Constants.SOURCES.IDS.GSUIT_COMMENT,
				authorName = `${activity.authorName} ${activity.extraHeaders && !anonymize ? activity.extraHeaders.from : "("+activity.sourceHandle+")"}`;

			obj.notes = [
				`<div><strong>Category:</strong> ${(([firstLetter, ...restOfWord]) => firstLetter.toUpperCase() + restOfWord.join(''))(category)}`,
				`<div><strong>Source:</strong> ${Constants.SOURCES.getDisplayNameFromId(activity.sourceId)}`,
				`<div><strong>Topic:</strong> ${uniqueTopicsArray.length ? uniqueTopicsArray.join(", ") : ""}`,
				`<div><strong>Associated Locations:</strong> ${("associatedLocations" in activity && activity.associatedLocations.length && activity.associatedLocations || []).map(l => l.name + " - " + l.address).join(", ")}`,
				`<div><br/><strong>Author Info:</strong> ${!isTwitter ?
					isGDoc ? activity.authors.split(",").join(", ") : authorName :
					`<a href='${activity.authorLink}' target='_blank' class='${classes.link}'>Author Profile</a>`}`,
				`<div><strong>Content:</strong> ${isTwitter ?
					`<a href='${activity.postLink}' target='_blank' class='${classes.link}'>View on Twitter</a>` : HTMLEntities.decode(activity.content)}`
			].join("</div>");

			setNotesObj(obj);
		}
		if (pattern  && Object.keys(pattern).length > 0) {

			let obj = {incidentDate: pattern.startedAt}, category = "Pattern",
			topicsArray = pattern.libraryTopics && pattern.libraryTopics.length ? pattern.libraryTopics.map(t => t.name ? t.name : "") : [],
			uniqueTopicsArray = [...new Set(topicsArray)];
			obj.notes = [
				`<div><strong>Category:</strong> ${(([firstLetter, ...restOfWord]) => firstLetter.toUpperCase() + restOfWord.join(''))(category)}`,
				`<div><strong>Topic:</strong> ${uniqueTopicsArray.length ? uniqueTopicsArray.join(", ") : ""}`,
				`<div><strong>Location:</strong> ${pattern.locationName}`,
				`<div><strong>Status:</strong> ${pattern.status}`,
				`<div><strong>Email:</strong> ${pattern.email}`,
				`<div><br/><strong>Signals:</strong><br/>${pattern.signals && pattern.signals.length ? pattern.signals.map(t => `<u>${t.title} </u>${t.content}`).join("<br/>") : ""}`,
				`<div><br/><strong>Alerts:</strong> ${pattern.counts.actionables}`,
				`<div><strong>Discussions:</strong> ${pattern.counts.discussions}`,
				`<div><strong>Emails:</strong> ${pattern.counts.emails}`,
				`<div><strong>Chats:</strong> ${pattern.counts.chats}</div>`,
			].join("</div>");

			setNotesObj(obj);
		}
	}, [activity, pattern, anonymize, classes]);

	useEffect(() => {
		buildReport();
	}, [buildReport]);

	const send = () => {
		closeHandler({...notesObj, ...{notes: `Detect User Notes: ${notes ? notes.notes : ""}<br /><br />` + notesObj.notes}});
	};

	let content = (
		<Grid
			container
			justify="center"
			direction="column"
			alignItems="stretch">
			<Grid item>
				<Typography className={classes.subtitle}>This will create a report, that includes everything below, in the <b>Behavioral Threat Assessment Manager</b> queue for review, and a case manager will be notified.</Typography>
			</Grid>
			<Grid item>
				<div
					className={classes.details}
					dangerouslySetInnerHTML={{__html: notesObj ? notesObj.notes : null}}>
				</div>
			</Grid>
			<Grid item className={classes.content}>
				<TextField
					autoFocus
					multiline
					fullWidth
					rows={5}
					name="notes"
					label="Notes"
					variant="outlined"
					placeholder="Notes..."
					helperText="Notes sent to Case Manager"
					onChange={notesChanged} />
			</Grid>
			<Grid item>
				<Grid
					container
					justify="flex-end"
					alignItems="center">
					{!smaller ? <Grid item className={classes.cancelBtnItem}>
						<Button
							color="primary"
							onClick={() => closeHandler(false)}>
							Cancel
						</Button>
					</Grid> : null}
					<Grid>
						<Button
							color="primary"
							variant="contained"
							onClick={send}>
							Send
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);

	return (
		<ResponsiveDialog
			open={open}
			maxWidth="sm"
			title="Send Report To Case Manager"
			content={content}
			hasCancel={!smaller}
			closeHandler={closeHandler}
			titleClassName={classes.dlgTitle} />
	);
}

ShareToBtaDialog.propTypes = {
	open: PropTypes.bool,
	activity: PropTypes.object,
	pattern: PropTypes.object,
	anonymize: PropTypes.bool,
	closeHandler: PropTypes.func.isRequired
};

export default ShareToBtaDialog;